import { useCallback, useEffect, useRef, useState } from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient';

/**
 * «درخواست‌های نوبت» — the reception's side of patient self-service booking.
 *
 * docs/PATIENT_CLINIC_CARE_CONTEXT_AND_SELF_SERVICE_DESIGN.md §9.1. The BFF has
 * already done the joining (patient, membership, slot conflict, age); this hook
 * owns only the four-state contract and the refetch, which is what the two
 * consumers — the full view and the day's strip — both need and neither should
 * write twice.
 *
 * The clinic is never a parameter: it travels as `x-active-clinic-id` on every
 * request (REV2 §A1), so a component cannot ask for another clinic's queue even
 * by mistake.
 */

export const REQUESTS_QUERY = `
  query AppointmentRequests($state: String, $providerId: ID, $limit: Int) {
    appointmentRequests(state: $state, providerId: $providerId, limit: $limit) {
      membershipStatus
      slotStillAvailable
      ageMinutes
      patient { id name phone fileNumber }
      appointment {
        id patientId placeholderLabel doctorId doctorName providerId
        dateTime time date type status urgency
        patientNote rejectionReason rejectionNote source requestedAt
      }
    }
  }
`;

export const PENDING_COUNT_QUERY = `
  query PendingAppointmentRequestCount {
    pendingAppointmentRequestCount
  }
`;

const CONFIRM_REQUEST = `
  mutation ConfirmAppointmentRequest($appointmentId: ID!, $newDateTime: String) {
    confirmAppointmentRequest(appointmentId: $appointmentId, newDateTime: $newDateTime) { id status dateTime }
  }
`;

const REJECT_REQUEST = `
  mutation RejectAppointmentRequest($appointmentId: ID!, $reasonCode: String!, $note: String) {
    rejectAppointmentRequest(appointmentId: $appointmentId, reasonCode: $reasonCode, note: $note) { id status }
  }
`;

/**
 * The list, plus the two answers the desk can give it.
 *
 * `state` is "open" or "handled". Kept as an argument rather than internal state
 * because the filter lives in the URL on the screen that owns it, and a hook that
 * held its own copy would be a second source of truth for the same fact.
 */
export function useAppointmentRequests({ state = 'open', providerId = '', enabled = true } = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  // Every read takes a ticket. Switching the filter while a read is in flight
  // would otherwise let the old answer land on the new filter — the same race
  // `useReception` solves the same way.
  const reqId = useRef(0);

  const reload = useCallback(async () => {
    if (!enabled) return;
    const req = ++reqId.current;
    setLoading(true);
    try {
      const data = await bffQuery(REQUESTS_QUERY, {
        state, providerId: providerId || undefined, limit: 200,
      });
      if (req !== reqId.current) return;
      setRows(data?.appointmentRequests || []);
      setError(null);
    } catch (err) {
      if (req !== reqId.current) return;
      // Four states, exactly one rendered: the rows are cleared so a stale list
      // cannot sit under an error banner and be acted on.
      setRows([]);
      setError(err?.message || String(err));
    } finally {
      if (req === reqId.current) setLoading(false);
    }
  }, [state, providerId, enabled]);

  useEffect(() => { reload(); }, [reload]);

  /**
   * تأیید, or «تأیید با زمان دیگر» when `newDateTime` is given.
   *
   * Never swallows the refusal: SLOT_TAKEN is the case the whole screen exists
   * for, and the caller turns it into «تأیید با زمان دیگر» rather than a toast
   * that says the confirmation failed.
   */
  const confirm = useCallback(async (appointmentId, newDateTime = null) => {
    const data = await bffMutation(CONFIRM_REQUEST, {
      appointmentId,
      newDateTime: newDateTime == null ? undefined : String(newDateTime),
    });
    await reload();
    return data?.confirmAppointmentRequest || null;
  }, [reload]);

  const reject = useCallback(async (appointmentId, reasonCode, note) => {
    const data = await bffMutation(REJECT_REQUEST, {
      appointmentId, reasonCode, note: note || undefined,
    });
    await reload();
    return data?.rejectAppointmentRequest || null;
  }, [reload]);

  return { rows, loading, error, reload, confirm, reject };
}

/**
 * Just the badge number.
 *
 * Its own query because the rail card and the nav row want the count without the
 * rows, and each row costs the BFF a patient read and a slot check. A failure is
 * null, not 0: «we could not ask» and «there are none» are different facts, and
 * only one of them should hide the strip.
 */
export function usePendingRequestCount({ enabled = true, refreshKey = 0 } = {}) {
  const [count, setCount] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    bffQuery(PENDING_COUNT_QUERY)
      .then((data) => {
        if (!live) return;
        const n = data?.pendingAppointmentRequestCount;
        setCount(typeof n === 'number' ? n : null);
      })
      .catch(() => { if (live) setCount(null); });
    return () => { live = false; };
  }, [enabled, refreshKey]);

  return count;
}
